import { useCallback, useState } from "react";
import { formatISO } from "date-fns";
import classNames from "classnames";
import { IntegrationTransaction } from "../../common/types/integrationTransaction";
import { EditableTableCellString } from "../accounting/editableTable/EditableTableCellString";
import { GridTableRow } from "../general/Tables/GridTable/GridTable";
import {
    IntegrationTransactionUpdateBody,
    useIntegrationTransactionUpdateMutation,
} from "../../api/integrationTransaction.api";
import { EditableTableCellDate } from "../accounting/editableTable/EditableTableCellDate";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";
import { EditableTableCell } from "../accounting/editableTable/EditableTableCell";
import editableTableStyles from "../accounting/editableTable/EditableTable.module.scss";
import { MonetaryInput } from "../forms/MonetaryInput";
import styles from "./ImporterPage.module.scss";

interface Props {
    transaction: IntegrationTransaction;
}

export const ImporterTransactionRow: React.FC<Props> = ({ transaction }) => {
    const [date, setDate] = useState<string | undefined>(
        formatISO(transaction.date, { representation: "date" }),
    );
    const [bankDescription, setBankDescription] = useState(
        transaction.bankDescription,
    );
    const [amount, setAmount] = useState<number | undefined>(
        transaction.amount,
    );

    const updateMutation = useIntegrationTransactionUpdateMutation(transaction);

    const update = useCallback(
        (body: IntegrationTransactionUpdateBody) => {
            updateMutation.mutate(body);
        },
        [updateMutation],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedUpdate = useDebouncedCallback(update, [], 1000);

    const updateDate = (value: string | null) => {
        setDate(value ?? undefined);
        if (value) {
            updateMutation.mutate({
                date: value,
            });
        }
    };

    const updateBankDescription = (value: string) => {
        setBankDescription(value);
        if (value) {
            debouncedUpdate({
                bankDescription: value,
            });
        }
    };

    const updateAmount = (value: number | undefined) => {
        setAmount(value);
        if (value) {
            debouncedUpdate({
                amount: value,
            });
        }
    };

    return (
        <GridTableRow columnBorders>
            <EditableTableCellDate
                value={date ?? ""}
                onChange={(value) => updateDate(value as string | null)}
                className={styles.editableCell}
            />
            <EditableTableCellString
                value={bankDescription}
                onChange={updateBankDescription}
                className={styles.editableCell}
            />
            <EditableTableCell className={styles.editableCell}>
                <MonetaryInput
                    value={amount}
                    onChange={updateAmount}
                    showCurrencySymbolIfEmpty={false}
                    allowNegative
                    className={classNames(
                        styles.transactionAmount,
                        editableTableStyles.editableTableInput,
                    )}
                />
            </EditableTableCell>
        </GridTableRow>
    );
};
